import React, { useState } from "react";
import { AutoFixHigh, HighlightAlt } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";

import { NavigationButton } from "ui";

import { selectVersionsByProjectId, useGetVersionConfigQuery } from "fond/api";
import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import CreateScenarioModal from "fond/project/versions/CreateScenarioModal";
import { WORKFLOW_KEYS, WorkflowKey } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";
import { BlockSpinner } from "fond/widgets";

import DataModificationPanel from "./DataModificationPanel";

type ScenarioTarget = "excludeDraw" | "optimize";

const scenarioDescriptions: Record<ScenarioTarget, string> = {
  excludeDraw: "This will create a new scenario version based on the original, with your excluded addresses applied.",
  optimize: "This will create a new scenario version based on the original, optimized for your financial objectives.",
};

interface AnalysisPanelProps {
  isDisabled?: boolean;
  workflowKey: WorkflowKey;
  /**
   * Whether to show the Optimize scenario option.
   */
  showOptimize?: boolean;
}

const AnalysisPanel: React.FC<AnalysisPanelProps> = ({ isDisabled, workflowKey, showOptimize = false }) => {
  const projectId = useAppSelector((state) => state.project.projectId);
  const versionId = useAppSelector((state) => state.project.versionId);
  const versions = useAppSelector((state) => selectVersionsByProjectId(state, projectId));
  const { isLoading } = useGetVersionConfigQuery(versionId);
  const [scenarioTarget, setScenarioTarget] = useState<ScenarioTarget | null>(null);

  const { workflowState } = useCustomWorkflow({ versionId, key: workflowKey });
  const { workflowState: optimizeWorkflowState } = useCustomWorkflow({
    versionId,
    key: showOptimize ? WORKFLOW_KEYS.COLLABORATION_CTS.CFO_ITERATION : undefined,
  });
  const isGeneratingCTS = [workflowState, optimizeWorkflowState].some((state) => state === "STARTING" || state === "IN_PROGRESS");

  if (isLoading) {
    return <BlockSpinner />;
  }

  return (
    <>
      {!isGeneratingCTS && (
        <>
          <Typography fontWeight="500">New Scenarios</Typography>
          <Box py={1}>
            <Typography variant="caption">Adjust your build scope to see the updated cost to serve analysis.</Typography>
          </Box>
          <NavigationButton
            data-testid="select-scenario-navigation"
            icon={<HighlightAlt />}
            title="Select"
            subtitle="Defer based on manual selection or cost."
            onClick={() => setScenarioTarget("excludeDraw")}
            sx={{ m: 0, mt: 1 }}
            disabled={isDisabled || isGeneratingCTS}
          />
          {showOptimize && (
            <NavigationButton
              data-testid="optimize-scenario-navigation"
              icon={<AutoFixHigh />}
              title="Optimize"
              subtitle="Defer based on your financial objectives."
              onClick={() => setScenarioTarget("optimize")}
              sx={{ m: 0, mt: 1 }}
              disabled={isDisabled || isGeneratingCTS}
            />
          )}
        </>
      )}

      <Box my={1}>
        <DataModificationPanel workflowKey={workflowKey} isDisabled={isDisabled} />
        {showOptimize && <DataModificationPanel workflowKey={WORKFLOW_KEYS.COLLABORATION_CTS.CFO_ITERATION} isDisabled={isDisabled} />}
      </Box>

      {scenarioTarget && (
        <CreateScenarioModal
          target={scenarioTarget}
          description={scenarioDescriptions[scenarioTarget]}
          onClose={() => {
            setScenarioTarget(null);
          }}
          versionNames={versions?.map((version) => version.Name)}
        />
      )}
    </>
  );
};

export default AnalysisPanel;
